import type { ImeiHubServiceId } from '../components/ImeiHubSignalIcon'
import type { ImeiCheckPayload, ImeiReportStatus, ImeiReportViewModel, ReportSection, SectionChipTone } from './imeiReportModel'
import { formatVerifyDeviceTitle } from './formatVerifyDeviceTitle'
import { followUpHubServiceForCheck } from './verifyCheckFollowUp'

export type VerifyCheckStatus = 'pass' | 'warning' | 'fail' | 'unverified' | 'not_applicable'

export type VerifyCheck = {
  id: string
  labelKey: string
  status: VerifyCheckStatus
  summary?: string
  detail?: string
  /** When unverified — hub service that can run this part of the report. */
  followUpService?: ImeiHubServiceId | null
}

export type VerifyReportVerdict = 'clean' | 'attention' | 'risk' | 'unknown' | 'invalid' | 'balance'

export type VerifyCoverageStats = {
  verified: number
  unverified: number
  notApplicable: number
  warning: number
  fail: number
}

export type VerifyReportModel = {
  verdict: VerifyReportVerdict
  deviceTitle: string
  imeiMasked: string
  checkedAtLabel: string
  luhnVerified: boolean
  /** Premium UI uses coverage, not numeric score (avoids implying 100% when checks are unverified). */
  coverageStats: VerifyCoverageStats
  resultHeadlineKey: string
  summary: string
  providerLabel: string
  checks: VerifyCheck[]
  criticalFindingKeys: string[]
  savedToProfile: boolean
  showTrustTheft: boolean
  showTrustIdentity: boolean
  showTrustRealtime: boolean
}

export function toneToVerifyStatus(tone: SectionChipTone): VerifyCheckStatus {
  switch (tone) {
    case 'ok':
      return 'pass'
    case 'warn':
      return 'warning'
    case 'risk':
      return 'fail'
    case 'muted':
      return 'not_applicable'
    default:
      return 'unverified'
  }
}

function verdictFromChecks(checks: VerifyCheck[]): VerifyReportVerdict | null {
  if (checks.some((c) => c.status === 'fail')) return 'risk'
  if (checks.some((c) => c.status === 'warning' || c.status === 'unverified')) return 'attention'
  return null
}

function summaryKeyFromChecks(checks: VerifyCheck[]): string | null {
  if (checks.some((c) => c.status === 'fail')) return 'vc.imeiReport.summary.previewSampleRisk'
  if (checks.some((c) => c.status === 'warning')) return 'vc.imeiReport.summary.previewSampleWarn'
  if (checks.some((c) => c.status === 'unverified')) return 'vc.imeiReport.summary.previewSampleUnknown'
  return null
}

function verdictFromStatus(status: ImeiReportStatus): VerifyReportVerdict {
  switch (status) {
    case 'clean':
      return 'clean'
    case 'warning':
      return 'attention'
    case 'high_risk':
      return 'risk'
    case 'invalid':
      return 'invalid'
    case 'balance':
      return 'balance'
    default:
      return 'unknown'
  }
}

function resultHeadlineKey(status: ImeiReportStatus, checks: VerifyCheck[]): string {
  if (status === 'high_risk') return 'vc.imeiReport.hero.high_risk'
  if (status === 'warning') return 'vc.imeiReport.hero.warning'
  if (status === 'invalid') return 'vc.imeiReport.hero.invalid'
  if (status === 'balance') return 'vc.imeiReport.hero.balance'
  if (checks.some((c) => c.status === 'fail')) return 'vc.imeiReport.hero.high_risk'
  if (checks.some((c) => c.status === 'warning')) return 'vc.imeiReport.hero.warning'
  return 'vc.imeiReport.result.noIssuesFound'
}

const SECTION_LABEL: Record<string, string> = {
  imei: 'vc.imeiReport.section.imei',
  device: 'vc.imeiReport.section.device',
  blacklist: 'vc.imeiReport.section.blacklist',
  carrier: 'vc.imeiReport.section.carrier',
  lock: 'vc.imeiReport.section.lock',
  finance: 'vc.imeiReport.section.finance',
  mdm: 'vc.imeiReport.section.mdm',
  android: 'vc.imeiReport.section.android',
}

function enrichCheckSummaries(
  checks: VerifyCheck[],
  translate: (key: string) => string,
): VerifyCheck[] {
  return checks.map((check) => {
    let summary = check.summary
    if (!summary && check.status === 'unverified') {
      summary = translate('vc.imeiReport.check.notChecked')
    }
    if (!summary && check.status === 'not_applicable') {
      summary = translate('vc.imeiReport.check.notApplicableShort')
    }
    if (!summary && check.id === 'blacklist' && check.status === 'pass') {
      summary = translate('vc.imeiReport.check.noTheftRecord')
    }
    return { ...check, summary, detail: check.detail ?? summary }
  })
}

export function mapToVerifyReportModel(args: {
  vm: ImeiReportViewModel
  data: ImeiCheckPayload
  sections: ReportSection[]
  imeiMasked: string
  checkedAtLabel: string
  serviceTitle: string
  summaryText: string
  savedToProfile: boolean
  hubService: ImeiHubServiceId
  translateDetail: (detail?: string) => string | undefined
  translateKey: (key: string) => string
}): VerifyReportModel {
  const {
    vm,
    data,
    sections,
    imeiMasked,
    checkedAtLabel,
    serviceTitle,
    summaryText,
    savedToProfile,
    hubService,
    translateDetail,
    translateKey,
  } = args

  let checks: VerifyCheck[] = sections.map((section) => {
    const status = toneToVerifyStatus(section.tone)
    const followUpService =
      status === 'unverified' ? followUpHubServiceForCheck(section.id, hubService) : null
    return {
      id: section.id,
      labelKey: section.titleKey ?? SECTION_LABEL[section.id] ?? section.id,
      status,
      summary: translateDetail(section.detail),
      detail: translateDetail(section.detail),
      followUpService,
    }
  })

  checks = enrichCheckSummaries(checks, translateKey)

  const checkVerdict = verdictFromChecks(checks)
  const verdict = checkVerdict ?? verdictFromStatus(vm.status)
  const isHubPreview =
    checks.length > 0 && checks.every((c) => c.id.startsWith('hub-preview-'))
  const previewSummaryKey = isHubPreview ? summaryKeyFromChecks(checks) : null
  const summary =
    previewSummaryKey != null ? translateKey(previewSummaryKey) : summaryText

  const coverageStats: VerifyCoverageStats = {
    verified: checks.filter((c) => c.status === 'pass').length,
    unverified: checks.filter((c) => c.status === 'unverified').length,
    notApplicable: checks.filter((c) => c.status === 'not_applicable').length,
    warning: checks.filter((c) => c.status === 'warning').length,
    fail: checks.filter((c) => c.status === 'fail').length,
  }

  const blacklistCheck = checks.find((c) => c.id === 'blacklist')
  const deviceCheck = checks.find((c) => c.id === 'device')
  const showTrustTheft = blacklistCheck?.status === 'pass'
  const showTrustIdentity = deviceCheck?.status === 'pass'
  const showTrustRealtime = Boolean(checkedAtLabel)

  const formattedTitle =
    formatVerifyDeviceTitle(data.brand, data.model) ||
    (vm.deviceLabel ? formatVerifyDeviceTitle(null, vm.deviceLabel.replace(/\s*·\s*/g, ' ')) : null)

  const deviceTitle = formattedTitle || serviceTitle

  return {
    verdict,
    deviceTitle,
    imeiMasked,
    checkedAtLabel,
    luhnVerified: Boolean(
      data.is_valid &&
        checks.some((c) => c.id === 'imei' && c.status === 'pass' && c.summary),
    ),
    coverageStats,
    resultHeadlineKey: resultHeadlineKey(vm.status, checks),
    summary,
    providerLabel: vm.providerLabel,
    checks,
    criticalFindingKeys: vm.criticalFindings,
    savedToProfile,
    showTrustTheft,
    showTrustIdentity,
    showTrustRealtime,
  }
}
